import { StartupSettings } from "../FeatureFlagProvider/types"
import { defaultSettings } from "../FeatureFlagProvider/default"

export type RetryHooks = {
  /** Return false to stop retrying immediately (e.g. non-retryable 4xx). */
  shouldRetry?: (error: unknown, attempt: number) => boolean
  /** Called before each wait — useful for logging/metrics. */
  onRetry?: (error: unknown, attempt: number, delayMs: number) => void
  /** Optional abort signal to cancel pending retries. */
  signal?: AbortSignal
}

const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new Error("Aborted"))
      return
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(timer)
      reject(signal?.reason ?? new Error("Aborted"))
    }
    signal?.addEventListener("abort", onAbort, { once: true })
  })

/**
 * Delay before a given retry attempt (1-based): exponential backoff,
 * capped at maxDelayMs, with symmetric jitter.
 */
export const computeBackoffDelay = (
  attempt: number,
  settings: StartupSettings
): number => {
  const exponential =
    (settings?.baseDelayMs ?? 100) * Math.pow((settings?.backoffMultiplier * 1), attempt - 1)
  const capped = Math.min(exponential, (settings?.maxDelayMs ?? 100))
  const jitter = capped * (settings?.jitterFactor ?? 0.1) * (Math.random() * 2 - 1)
  return Math.max(0, Math.round(capped + jitter))
}

/**
 * Runs `fn`, retrying on rejection with exponential backoff + jitter.
 * `settings` is merged over `defaultSettings`, so it works with zero config
 * outside of the FeatureFlag context — or with any partial override.
 * Rethrows the last error once retries are exhausted or `shouldRetry` returns false.
 */
export const withRetry = async <T>(
  fn: (attempt: number) => Promise<T>,
  settings: Partial<StartupSettings> = {},
  hooks: RetryHooks = {}
): Promise<T> => {
  const config: StartupSettings = { ...defaultSettings, ...settings }
  const { shouldRetry, onRetry, signal } = hooks

  let lastError: unknown

  const retries = config?.maxRetries ?? 5

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (signal?.aborted) throw signal.reason ?? new Error("Aborted")

    try {
      return await fn(attempt)
    } catch (error) {
      lastError = error

      const isLastAttempt = attempt === retries
      if (isLastAttempt || (shouldRetry && !shouldRetry(error, attempt))) {
        throw error
      }

      const delayMs = computeBackoffDelay(attempt + 1, config)
      onRetry?.(error, attempt + 1, delayMs)
      await sleep(delayMs, signal)
    }
  }

  throw lastError // unreachable, satisfies the type checker
}
